import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { WebcontentService } from '../../../services/webcontent.service';

@Component({
  selector: 'app-submenu',
  standalone: true,
  imports: [CommonModule, MenuModule, RouterModule, TieredMenuModule, ListboxModule, TranslateModule],
  template: `
    <ul [ngClass]="level === 0 ? 'submenu-item-container' : 'submenu-subitem-container'" [style.marginLeft]="paddingLeftValue">
      <ng-container *ngFor="let item of item.items; let i = index">
        <li [ngClass]="level === 0 ? 'submenu-item' : 'submenu-subitem'" [style]="{ listStyleType: 'none' }">
          <a
            [attr.href]="item.url"
            [ngClass]="activeItem === i ? 'active' : ''"
            [attr.target]="item.kind === 'link' ? '_blank' : null"
            tabindex="0"
            pRipple
            class="layout-menuitem-link"
            [class.current-page]="!!currentId && item.id === currentId"
            (click)="onSelect(item, i)"
            style="cursor: pointer"
          >
            <span
              class="layout-menuitem-text"
              [style]="{ listStyleType: 'none', fontSize: fontSizeValue }"
              >{{ item.label ?? '' | translate }}</span
            >
            <i
              class="pi pi-fw pi-angle-down layout-submenu-toggler"
              style="color: #00205B"
              *ngIf="item.hasChildren && activeItem === i"
            ></i>
            <i
              class="pi pi-fw pi-angle-right layout-submenu-toggler"
              style="color: #A6A09B"
              *ngIf="item.hasChildren && activeItem !== i"
            ></i>
          </a>
          <ng-container *ngIf="activeItem === i">
            <app-submenu
              *ngFor="let subitem of selectedLevels"
              [item]="subitem"
              [level]="level + 1"
              [expandedPath]="nestedPath"
              [currentId]="currentId"
            />
          </ng-container>
        </li>
      </ng-container>
    </ul>
  `,
  styleUrls: ['./submenu.component.scss'],
})
export class AppSubMenu implements OnInit {
  constructor(private router: Router, private webcontentService: WebcontentService) { }

  @Input() item: any;
  @Input() level: number;
  // Id dei nodi da espandere automaticamente, a partire da questo livello
  @Input() expandedPath: string[] = [];
  // Id della pagina corrente, evidenziata nel menu
  @Input() currentId: string | null = null;

  selectedLevels: any[] = []; // livelli successivi
  activeItem: number | null = null;
  nestedPath: string[] = []; // percorso da espandere nel livello successivo
  paddingLeftValue: string;
  fontSizeValue: string;

  ngOnInit(): void {
    this.paddingLeftValue = `${this.level > 0 ? this.level * 13 : 0}px`;
    this.fontSizeValue = `calc(16px - (${this.level * 1}px))`;
    this.expandPath();
  }

  private expandPath(): void {
    const [targetId, ...remainingPath] = this.expandedPath ?? [];
    if (!targetId) {
      return;
    }
    const index = this.item?.items?.findIndex((child: any) => child.id === targetId) ?? -1;
    if (index === -1) {
      return;
    }
    const target = this.item.items[index];
    this.activeItem = index;
    this.nestedPath = remainingPath;

    if (target.items && target.items.length > 0) {
      this.selectedLevels.push(target);
    } else if (target.hasChildren) {
      this.webcontentService.getPages(target.id).subscribe((children) => {
        target.items = children.map((child) => this.transformNode(child));
        this.selectedLevels.push(target);
      });
    }
  }

  onSelect(item: any, level: number) {
    if (this.activeItem === level) {
      this.activeItem = null;
    } else {
      this.selectedLevels = [];
      this.nestedPath = [];
      this.activeItem = level;

      if (item.kind === 'link') {
        window.open(item.url, '_blank');
      }

      if (item.hasChildren && item.kind !== 'link') {
        if (item.items && item.items.length > 0) {
          this.selectedLevels.push(item);
        } else {
          this.webcontentService.getPages(item.id).subscribe((children) => {
            const items = children.map((child) => this.transformNode(child));
            item.items = items;
            this.selectedLevels.push(item);
          });
        }
      }
    }

    if (item.kind === 'page') {
      if (item.slug) {
        this.router.navigate(['/contents', item.slug], {
        });
      } else if (item.id) {
        this.router.navigate(['/contents', item.id], {
        });
      }
    }
  }

  transformNode(node: any) {
    return {
      label: node.menuTitle,
      id: node.id,
      url: node.url,
      icon: '', // puoi impostare un'icona personalizzata qui
      kind: node.kind,
      slug: node.slug,
      parentNode: node.parent,
      hasChildren: node.hasChildren,
      items: [],
    };
  }
}
